// Adapted from https://github.com/mui/material-ui/tree/b121bd7359e6d0bb01890271d047da159cfb2b08/packages/mui-lab/src/TabList/
//
// The MIT License (MIT)
//
// Copyright (c) 2014 Call-Em-All
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

import { OverrideProps } from '@mui/material/OverridableComponent';
import tabsClasses from 'src/components/Tabs/tabsClasses';
import clsx from 'clsx';
import Tabs, {
  TabsTypeMap,
  TabsProps as MuiTabsProps,
} from '@mui/material/Tabs';
import { DistributiveOmit } from '@mui/types';
import * as React from 'react';
import {
  getPanelId,
  getTabId,
  TabContextValue,
  useTabContext,
} from 'src/components/TabContext/TabContext';
import { AnyProps } from 'src/types/util';
import {
  ExtendTypeMap,
  forwardRefToOverridableComponent,
} from 'src/utils/forwardRefToOverridableComponent';

//
// The TabList component was copied from MUI mainly because it's tightly coupled
// to the TabPanel implementation, which was also copied from MUI.
//

export interface TabListOwnProps {
  /**
   * A list of `<Tab />` elements.
   */
  children?: React.ReactNode;

  /**
   * Callback fired when the value changes.
   *
   * @param event The event source of the callback. **Warning**: This is a generic event not a change event.
   * @param value The `value` prop of the selected {@link Tab}, or `undefined` if no Tab is selected or if the Tab does not have a value.
   */
  onChange?: (event: React.SyntheticEvent, value: string | undefined) => void;

  /**
   * An optional custom `React.Context` to use for the TabList.
   *
   * This is only necessary in rare cases where multiple `TabContext` providers
   * are nested within each other.
   */
  context?: React.Context<TabContextValue>;

  /**
   * A boolean that a divider on the TabList when set to true.
   * @default false
   */
  divider?: boolean;
}

/**
 * Deprecate props that aren't supported in Unify.
 */
type UnsupportedProps = {
  /**
   * @deprecated Unsupported prop: This prop is not supported in Unify design guidelines and will be removed in the next major.
   * Please reach out to us in #unify-design-system-support with your designer if you have a use case for this prop.
   */
  indicatorColor?: MuiTabsProps['indicatorColor'];
  /**
   * @deprecated Unsupported prop: This prop is not supported in Unify design guidelines and will be removed in the next major.
   * Please reach out to us in #unify-design-system-support with your designer if you have a use case for this prop.
   */
  textColor?: MuiTabsProps['textColor'];
};

export type TabListTypeMap<
  AdditionalProps = unknown,
  // Use div since Tabs's defaultComponent seems to incorrectly be `typeof ButtonBase`.
  RootComponent extends React.ElementType = 'div'
> = ExtendTypeMap<
  {
    props: DistributiveOmit<
      TabsTypeMap['props'],
      'children' | 'value' | 'onChange' | keyof UnsupportedProps
    >;
    defaultComponent: RootComponent;
  },
  AdditionalProps & TabListOwnProps & UnsupportedProps,
  RootComponent
>;

export type TabListClassKey = keyof NonNullable<
  TabListTypeMap['props']['classes']
>;

export type TabListProps<
  RootComponent extends React.ElementType = TabListTypeMap['defaultComponent'],
  AdditionalProps = unknown
> = OverrideProps<
  TabListTypeMap<AdditionalProps, RootComponent>,
  RootComponent
>;

/**
 * TabList renders the row of tab buttons that control which TabPanel is visible.
 *
 * Demos:
 *
 * - [Tabs — TabList](https://unify.workiva.org/components/tabs?tab=code)
 *
 * API:
 *
 * - [TabList API](https://unify.workiva.org/components/tabs?tab=props#tablist)
 */
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const TabList = forwardRefToOverridableComponent<
  TabListTypeMap,
  TabListProps
>(function TabList(props, ref) {
  const {
    context: contextProp,
    children: childrenProp,
    onChange: onChangeProp,
    divider = false,
    orientation = 'horizontal',
    className,
    ...other
  } = props;

  const context = useTabContext(contextProp);
  if (context === null) {
    throw new TypeError(
      'No TabContext provided; make sure your `TabList` and `TabPanel`s are wrapped in a `TabContext`.'
    );
  }

  const classes = clsx(
    divider && orientation === 'horizontal' && tabsClasses.horizontalDividers,
    divider && orientation === 'vertical' && tabsClasses.verticalDividers,
    className
  );

  const children = React.Children.map(childrenProp, (child) => {
    if (React.isValidElement<AnyProps>(child)) {
      return React.cloneElement(child, {
        // SOMEDAY: `Tabs` will set those themselves
        'aria-controls': getPanelId(context, child.props.value),
        id: getTabId(context, child.props.value),
      });
    }

    return null;
  });
  const onChange: TabListProps['onChange'] = React.useCallback(
    (event: React.SyntheticEvent, value: string | number | undefined) =>
      // Tabs.onChange gets called with the number index of the Tab if it has no `value` prop.
      // Return undefined in that case so that we can type the arg as `string` in our TabsList.onChange callback.
      onChangeProp?.(event, typeof value === 'string' ? value : undefined),
    [onChangeProp]
  );

  return (
    <Tabs
      // Set `component` to div, matching the typing of TabListProps and the forwarded ref,
      // so that spreading props and setting ref on Tabs doesn't result in type errors
      // where it expects a button instead of a div.
      // This should not cause any functional changes, since Tabs defaults to using a div
      // even though its defaultComponent seems to incorrectly be `typeof ButtonBase`.
      component="div"
      className={classes}
      orientation={orientation}
      {...other}
      ref={ref}
      /* The Tabs component uses `false` instead of nullish values to indicate no tab is selected. */
      value={context.value ?? false}
      onChange={onChange}
    >
      {children}
    </Tabs>
  );
});

export default TabList;
